// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * How far a pixel of the aerial is, on the ground.
 *
 * An uploaded drawing has to be told its scale, because a PDF is just ink and
 * nobody knows what it was plotted at. A map does not: a web map is a
 * projection with a zoom level and a latitude, and the distance across a pixel
 * follows from those exactly. Asking somebody to calibrate an aerial by
 * clicking two points and typing a number they would have to guess is asking
 * them to re-measure something the map already knows — and until they did,
 * nothing on the map could be measured at all.
 *
 * So the map reports its own scale and the drawing uses it. The only thing
 * this module does is the arithmetic in between, which is small enough to be
 * worth getting exactly right.
 */

const FEET_PER_METRE = 3.280839895013123;

/**
 * Feet per pixel, from metres per pixel.
 *
 * Null rather than a number for anything that cannot be a distance: a scale
 * that is quietly wrong is worse than one that is plainly absent, because
 * every length and area on the drawing inherits it.
 */
export function feetPerPixelFrom(metresPerPixel: number | null | undefined): number | null {
  if (metresPerPixel === null || metresPerPixel === undefined) return null;
  if (!Number.isFinite(metresPerPixel) || metresPerPixel <= 0) return null;
  return metresPerPixel * FEET_PER_METRE;
}

/**
 * Metres per pixel at a given latitude and web-map zoom level.
 *
 * The standard spherical-Mercator relation: the equator is 40,075,016.686 m
 * around and 256 pixels wide at zoom 0, and everything else is that times the
 * cosine of the latitude. Offered for tests and for callers without a live
 * map; a real map is asked directly, because it knows its own projection.
 */
export function metresPerPixel(latitude: number, zoom: number, tileSize = 256): number | null {
  if (!Number.isFinite(latitude) || Math.abs(latitude) > 85.06) return null;
  if (!Number.isFinite(zoom) || zoom < 0 || zoom > 24) return null;
  if (!Number.isFinite(tileSize) || tileSize <= 0) return null;
  const equator = 40075016.686;
  return (equator * Math.cos((latitude * Math.PI) / 180)) / (tileSize * Math.pow(2, zoom));
}

/**
 * What to say about a scale that came from the map.
 *
 * Deliberately approximate. "1 inch = 20 feet" is a true statement about a
 * sheet of paper and only a guess about a screen, whose inch depends on the
 * display, so the note says about, and the graphic bar on the sheet remains
 * the honest answer.
 */
export function mapScaleNote(feetPerPixel: number | null | undefined, dpi = 96): string {
  if (!feetPerPixel || !Number.isFinite(feetPerPixel) || feetPerPixel <= 0) return "";
  const perInch = feetPerPixel * dpi;
  // Rounded the way a scale is written: to 5' under 100, to 10' above.
  const step = perInch < 100 ? 5 : 10;
  const nice = Math.max(step, Math.round(perInch / step) * step);
  return `about 1" = ${nice}'`;
}
